import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './RegistrSelector.css';

const RegistrSelector = ({
  subject = [], 
  placeholder = 'Выберите услугу...',
  selected = '',
  onSelect,
  multiple = false,
  maxSelect = Infinity,
  disabled = false,
  isMetroSelector = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [realSearchValue, setRealSearchValue] = useState('');
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [selectedList, setSelectedList] = useState([]);
  
  const [expandedGroups, setExpandedGroups] = useState({});
  const [activeStickyGroup, setActiveStickyGroup] = useState(null);
  const [selectedCategoryByService, setSelectedCategoryByService] = useState(null);
  
  const dropdownRef = useRef(null);
  const dropdownListRef = useRef(null);
  const selectedOptionRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const scrollTrackRef = useRef(null);
  const groupHeadersRef = useRef({});
  const [isDragging, setIsDragging] = useState(false);

  const isGrouped = useMemo(() => (
    Array.isArray(subject) && subject.length > 0 &&
    subject[0] != null && typeof subject[0] === 'object' &&
    !Array.isArray(subject[0]) && 'title' in subject[0]
  ), [subject]);

  const allItems = useMemo(() => (isGrouped
    ? subject.flatMap(g => Array.isArray(g.items) ? g.items : []) 
    : Array.isArray(subject) ? subject : []), [isGrouped, subject]);

  // Функция для переключения видимости группы
  const toggleGroup = (groupIndex, e) => {
    if (e) {
      e.stopPropagation();
    }
    setExpandedGroups(prev => ({
      ...prev,
      [groupIndex]: !prev[groupIndex]
    }));
  };

  // SYNC
  useEffect(() => {
    if (multiple) {
      setSelectedList(Array.isArray(selected) ? selected : []);
    } else {
      const val = typeof selected === 'string' ? selected : '';
      setSelectedActivity(val);
      setSearchValue(val);
      
      if (isGrouped && val) {
        let categoryIndex = null;
        subject.forEach((group, index) => {
          if (Array.isArray(group.items) && group.items.includes(val)) {
            categoryIndex = index;
          }
        });
        setSelectedCategoryByService(categoryIndex);
      }
    }
    
    if (isGrouped) {
      const initialExpanded = {};
      subject.forEach((group, index) => {
        initialExpanded[index] = true;
      });
      setExpandedGroups(initialExpanded);
    }
  }, [selected, multiple, subject, isGrouped]);

  // Sticky header effect
  useEffect(() => {
    if (!isOpen || !isGrouped || !dropdownListRef.current) return;

    const listElement = dropdownListRef.current;
    const groupElements = groupHeadersRef.current;

    const handleScroll = () => {
      const scrollTop = listElement.scrollTop;
      const listRect = listElement.getBoundingClientRect();
      
      let currentGroup = null;
      let maxBottom = -Infinity;
      
      Object.entries(groupElements).forEach(([index, element]) => {
        if (!element) return;
        
        const rect = element.getBoundingClientRect();
        const relativeTop = rect.top - listRect.top + scrollTop;
        
        if (relativeTop <= scrollTop + 50 && relativeTop > maxBottom) {
          currentGroup = parseInt(index);
          maxBottom = relativeTop;
        }
      });
      
      setActiveStickyGroup(currentGroup);
    };

    listElement.addEventListener('scroll', handleScroll);
    const initialScrollTimer = setTimeout(handleScroll, 100);
    
    return () => {
      listElement.removeEventListener('scroll', handleScroll);
      clearTimeout(initialScrollTimer);
    };
  }, [isOpen, isGrouped, subject]);

  // Click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (!multiple) {
          setSearchValue(selectedActivity || '');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedActivity, multiple]);

  // Scroll thumb
  const updateScrollThumb = useCallback(() => {
    if (!dropdownListRef.current || !scrollThumbRef.current || !scrollTrackRef.current) return;
    
    const content = dropdownListRef.current;
    const thumb = scrollThumbRef.current;
    const track = scrollTrackRef.current;
    
    const scrollTop = content.scrollTop;
    const scrollHeight = content.scrollHeight;
    const clientHeight = content.clientHeight;
    const scrollable = scrollHeight - clientHeight;
    
    if (scrollable <= 0) {
      thumb.style.height = '0px';
      thumb.style.opacity = '0';
      return;
    }
    
    const trackHeight = track.clientHeight;
    const thumbHeight = Math.max((clientHeight / scrollHeight) * trackHeight, 30);
    const progress = scrollTop / scrollable;
    const maxTranslate = trackHeight - thumbHeight;
    const translateY = progress * maxTranslate;
    
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.setProperty('--thumb-position', `${translateY}px`);
    thumb.style.opacity = '1';
  }, []);

  useEffect(() => {
    if (isOpen && dropdownListRef.current) {
      updateScrollThumb();
      dropdownListRef.current.addEventListener('scroll', updateScrollThumb);
    }
    return () => {
      if (dropdownListRef.current) {
        dropdownListRef.current.removeEventListener('scroll', updateScrollThumb);
      }
    };
  }, [isOpen, updateScrollThumb]);

  // Drag scrollbar
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !dropdownListRef.current || !scrollTrackRef.current) return;
      e.preventDefault();
      
      const track = scrollTrackRef.current.getBoundingClientRect();
      const list = dropdownListRef.current;
      const scrollable = list.scrollHeight - list.clientHeight;
      const relY = e.clientY - track.top;
      const progress = Math.min(Math.max(relY / track.height, 0), 1);
      list.scrollTop = progress * scrollable;
    };
    
    const handleMouseUp = () => setIsDragging(false);
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleTrackClick = (e) => {
    if (!dropdownListRef.current || !scrollTrackRef.current) return;
    
    const track = scrollTrackRef.current.getBoundingClientRect();
    const list = dropdownListRef.current;
    const scrollable = list.scrollHeight - list.clientHeight;
    const relY = e.clientY - track.top;
    const progress = Math.min(Math.max(relY / track.height, 0), 1);
    list.scrollTop = progress * scrollable;
  };

  // Input handlers
  const handleInputChange = (e) => {
    const val = e.target.value;
    if (multiple) {
      setRealSearchValue(val);
    } else {
      setSearchValue(val);
    }
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (!multiple) {
      setSearchValue('');
    }
  };

  // Filtering
  const searchText = (multiple ? realSearchValue : searchValue || '').trim().toLowerCase();
  
  const filteredItems = useMemo(() => searchText
    ? allItems.filter(item => {
        if (React.isValidElement(item)) {
          const children = item.props.children;
          if (Array.isArray(children)) {
            const textSpan = children.find(child => 
              React.isValidElement(child) && child.type === 'span'
            );
            if (textSpan) {
              return textSpan.props.children.toLowerCase().includes(searchText);
            }
          }
          return false;
        }
        return typeof item === 'string' && item.toLowerCase().includes(searchText);
      })
    : allItems, [allItems, searchText]);

  useEffect(() => {
    if (isOpen) {
      setShowScrollbar(filteredItems.length > 5);
    }
  }, [isOpen, filteredItems.length]);

  // Selection
  const handleSelectSingle = (item) => {
    setSelectedActivity(item);
    setSearchValue(item);
    onSelect?.(item);
    setIsOpen(false);
    
    if (isGrouped) {
      let categoryIndex = null;
      subject.forEach((group, index) => {
        if (Array.isArray(group.items) && group.items.includes(item)) {
          categoryIndex = index;
        }
      });
      setSelectedCategoryByService(categoryIndex);
    }
  };

  const handleSelectMultiple = (item) => {
    let itemId = item;
    if (React.isValidElement(item)) {
      itemId = item.props['data-value'] || item.props['data-metro-id'] || item;
    }
    
    const current = Array.isArray(selected) ? selected : [];
    let updated = current.includes(itemId)
      ? current.filter(i => i !== itemId)
      : [...current, itemId];

    if (updated.length > maxSelect) return;
    onSelect?.(updated);
    setRealSearchValue('');
  };

  const getItemText = (item) => {
    if (React.isValidElement(item)) {
      const children = item.props.children;
      if (Array.isArray(children)) {
        const textSpan = children.find(child => 
          React.isValidElement(child) && child.type === 'span'
        );
        if (textSpan) {
          return textSpan.props.children;
        }
      }
      return '';
    }
    return item;
  };

  const getSortedItems = (items) => {
    if (!multiple || !selectedList.length || !isMetroSelector) return items;
    
    return [...items].sort((a, b) => {
      const aId = a?.props?.['data-value'] || a?.props?.['data-metro-id'] || a;
      const bId = b?.props?.['data-value'] || b?.props?.['data-metro-id'] || b;
      
      const aSelected = selectedList.includes(aId);
      const bSelected = selectedList.includes(bId);
      
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  };

  const getInputValue = () => {
    if (isOpen) {
      return multiple ? realSearchValue : searchValue;
    }
    
    if (multiple) {
      const selectedArray = Array.isArray(selected) ? selected : [];
      const count = selectedArray.length;
      
      if (count === 0) return '';
      
      if (isMetroSelector) {
        if (count === 1) {
          const item = allItems.find(i => {
            const itemId = i?.props?.['data-value'] || i?.props?.['data-metro-id'] || i;
            return itemId === selectedArray[0];
          });
          return getItemText(item) || 'Выбрана 1 станция';
        }
        return `Выбрано ${count} ${count === 1 ? 'станция' : count < 5 ? 'станции' : 'станций'}`;
      } else {
        return selectedArray.join(', ');
      }
    }
    
    return selectedActivity || '';
  };

  const inputValue = getInputValue();

  const renderRegularItem = (item, isSelected, isDisabled, index) => (
    <div
      key={index}
      ref={!multiple && isSelected ? selectedOptionRef : null}
      className={`activity-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
      onClick={() => {
        if (isDisabled || disabled) return;
        multiple ? handleSelectMultiple(item) : handleSelectSingle(item);
      }}
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        padding: '8px 15px',
        margin: '0 15px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        backgroundColor: isSelected ? '#E6F0FA' : 'transparent',
      }}
    >
      {multiple && (
        <div className="custom-checkbox-wrapper">
          <div className={`custom-checkbox ${isSelected ? 'checked' : ''}`}>
            {isSelected && (
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
        </div>
      )}
      <span style={{ marginLeft: multiple ? '10px' : '0' }}>{item}</span>
    </div>
  );

  const renderMetroItem = (item, isSelected, isDisabled, index) => {
    if (React.isValidElement(item)) {
      return (
        <div
          key={index}
          className={`activity-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!isDisabled && !disabled) {
              handleSelectMultiple(item);
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 15px',
            margin: '0 15px',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
            backgroundColor: isSelected ? '#E6F0FA' : 'transparent',
          }}
        >
          {multiple && (
            <div className="custom-checkbox-wrapper">
              <div className={`custom-checkbox ${isSelected ? 'checked' : ''}`}>
                {isSelected && (
                  <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                    <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </div>
          )}
          
          {React.cloneElement(item, {
            style: {
              ...item.props.style,
              marginLeft: multiple ? '10px' : '0',
              flex: 1,
            }
          })}
        </div>
      );
    }
    
    return renderRegularItem(item, isSelected, isDisabled, index);
  };

  return (
    <div className="activity-input-container" ref={dropdownRef}>
      <input
        type="text"
        className={`activity-input ${isOpen ? 'active' : ''}`}
        placeholder={placeholder}
        value={inputValue}
        onFocus={handleInputFocus}
        onChange={handleInputChange}
        readOnly={disabled}
        disabled={disabled}
      />
      <div 
        className={`arrow ${isOpen ? 'arrow-up' : 'arrow-down'}`}
        onClick={() => {
          if (disabled) return;
          setIsOpen(prev => !prev);
          if (!multiple) {
            setSearchValue('');
          }
        }}
      >
        <svg width="20" height="12" viewBox="0 0 12 8" fill="none">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      
      {isOpen && (
        <div className="dropdown-wrapper">
          <div className="activity-dropdown">
            <div 
              className="dropdown-content"
              ref={dropdownListRef}
              style={{ paddingRight: showScrollbar ? '10px' : '0', height: '100%' }}
            >
              {filteredItems.length === 0 ? (
                <div className="activity-option" style={{ 
                  color: '#999', 
                  padding: '12px',
                  margin: '0 15px',
                }}>
                  {searchText ? 'Ничего не найдено' : 'Нет данных'}
                </div>
              ) : isGrouped ? (
                subject.map((group, groupIdx) => {
                  const groupItems = Array.isArray(group.items) ? group.items : [];
                  const sortedItems = isMetroSelector ? getSortedItems(groupItems) : groupItems;
                  
                  const visibleItems = sortedItems.filter(item => {
                    if (!searchText) return true;
                    
                    if (React.isValidElement(item)) {
                      const children = item.props.children;
                      if (Array.isArray(children)) {
                        const textSpan = children.find(child => 
                          React.isValidElement(child) && child.type === 'span'
                        );
                        if (textSpan) {
                          return textSpan.props.children.toLowerCase().includes(searchText);
                        }
                      }
                      return false;
                    }
                    return typeof item === 'string' && item.toLowerCase().includes(searchText);
                  });
                  
                  if (visibleItems.length === 0 && searchText) return null;
                  
                  const isGroupExpanded = expandedGroups[groupIdx] && !searchText;
                  
                  return (
                    <React.Fragment key={groupIdx}>
                      <div 
                        ref={el => groupHeadersRef.current[groupIdx] = el}
                        className="group-header"
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px',
                          margin: groupIdx === 0 ? '0 15px 8px 15px' : '8px 15px 8px 15px',
                          borderRadius: '8px',
                          border: '1px solid #02283D',
                          backgroundColor: 'transparent',
                          color: '#02283D',
                          fontWeight: 500,
                          fontSize: '16px',
                        }}
                        onClick={(e) => toggleGroup(groupIdx, e)}
                      >
                        <span>
                          {group.title || 'Без названия'} 
                          {!searchText && ` (${visibleItems.length})`}
                        </span>
                        {!searchText && (
                          <span style={{ fontSize: '16px' }}>
                            {isGroupExpanded ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                      
                      {(isGroupExpanded || searchText) && visibleItems.map((item, idx) => {
                        const itemId = item?.props?.['data-value'] || item?.props?.['data-metro-id'] || item;
                        const isSelected = multiple ? selectedList.includes(itemId) : item === selectedActivity;
                        const isDisabled = multiple && !isSelected && selectedList.length >= maxSelect;
                        
                        return isMetroSelector 
                          ? renderMetroItem(item, isSelected, isDisabled, idx)
                          : renderRegularItem(item, isSelected, isDisabled, idx);
                      })}
                    </React.Fragment>
                  );
                })
              ) : (
                (isMetroSelector ? getSortedItems(filteredItems) : filteredItems).map((item, index) => {
                  const isSelected = multiple ? selectedList.includes(item) : item === selectedActivity;
                  const isDisabled = multiple && !isSelected && selectedList.length >= maxSelect;
                  
                  return isMetroSelector
                    ? renderMetroItem(item, isSelected, isDisabled, index)
                    : renderRegularItem(item, isSelected, isDisabled, index);
                })
              )}
            </div>
            
            {showScrollbar && (
              <div className="custom-scrollbar">
                <div 
                  className="scroll-track" 
                  ref={scrollTrackRef}
                  onClick={handleTrackClick}
                />
                <div 
                  className="scroll-thumb" 
                  ref={scrollThumbRef}
                  onMouseDown={handleThumbMouseDown}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrSelector;
